import {
  VerticalBarChart as FluentVerticalBarChart,
  type VerticalBarChartDataPoint,
} from '@fluentui/react-charts';
import { makeStyles, tokens } from '@fluentui/react-components';
import { useMemo } from 'react';

import { seriesColor, toneToColor } from './colors';
import { formatValue, tickFormatter } from './format';
import type { BarPoint, ChartTone, ChartUnit } from './types';

interface BarChartProps {
  bars: readonly BarPoint[];
  /** Status tint applied uniformly when bars don't carry their own color. */
  tone?: ChartTone;
  unit?: ChartUnit;
  yMax?: number;
  yMin?: number;
  height?: number;
  /** Bar width in px or 'auto' to calculate from padding. */
  barWidth?: number | 'auto' | 'default';
  chartTitle?: string;
}

const useStyles = makeStyles({
  wrapper: {
    width: '100%',
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Vertical bar chart — categorical (e.g. requests by route) or time-bucketed
 * (e.g. signed docs per day for the last 30 days).
 */
export function BarChart({
  bars,
  tone,
  unit,
  yMax,
  yMin = 0,
  height = 220,
  barWidth = 16,
  chartTitle,
}: BarChartProps) {
  const classes = useStyles();

  const data: VerticalBarChartDataPoint[] = useMemo(
    () =>
      bars.map((b, i) => ({
        // BarPoint already constrains `x` to string | number | Date — pass
        // through. Callers that have epoch-ms values should wrap them in
        // `new Date(...)` themselves so a numeric `x` stays a categorical
        // bucket, not a coerced timeline.
        x: b.x,
        y: b.y,
        color: b.color ?? (tone ? toneToColor(tone) : seriesColor(i)),
        legend: typeof b.x === 'string' ? b.x : undefined,
        yAxisCalloutData: formatValue(b.y, unit),
      })),
    [bars, tone, unit],
  );

  return (
    <div className={classes.wrapper}>
      <FluentVerticalBarChart
        data={data}
        height={height}
        yMinValue={yMin}
        yMaxValue={yMax}
        yAxisTickFormat={tickFormatter(unit)}
        barWidth={barWidth}
        chartTitle={chartTitle}
        hideLegend
        reflowProps={{ mode: 'min-width' }}
      />
    </div>
  );
}
